import React, { useState } from 'react';
import { X, Package, Shield, Factory, ShoppingCart, ShoppingBag, UserCheck, Award, Truck, BarChart3, DollarSign, Layers, Users, Warehouse, Box } from 'lucide-react';

interface ModuleOption {
  id: number;
  name: string;
  displayName: string;
  description: string;
  category?: string;
}

export interface CompanyFormValues {
  name: string;
  email: string;
  phone: string;
  companyName: string;
  password: string;
  permissions: { moduleId: number; canView: boolean; canCreate: boolean; canUpdate: boolean; canDelete: boolean }[];
}

export interface CompanyFormInitial {
  name: string;
  email: string;
  phone: string;
  companyName: string;
  permissions: { moduleId: number }[];
}

interface CreateCompanyModalProps {
  modules: ModuleOption[];
  initial?: CompanyFormInitial | null;
  onClose: () => void;
  onSubmit: (values: CompanyFormValues) => void;
  submitting?: boolean;
}

const MODULE_ICONS: Record<string, any> = {
  'chip-management': Package,
  'gps-management': Shield,
  machines: Factory,
  purchases: ShoppingCart,
  sales: ShoppingBag,
  clients: UserCheck,
  qualities: Award,
  stock: Warehouse,
  'yarn-outward': Truck,
  'yarn-inward': Box,
  'yarn-dashboard': BarChart3,
  'taka-inventory': Box,
  'beam-master': Box,
  production: BarChart3,
  salary: DollarSign,
  beams: Layers,
  employees: Users,
};

// Matches the sidebar's own grouping (Layout.tsx's INVENTORY_MODULES) so a reseller
// sees the same "Inventory" cluster here that they already know from their own nav,
// instead of five indistinguishable flat rows.
const INVENTORY_MODULE_NAMES = new Set([
  'stock',
  'yarn-inward',
  'yarn-dashboard',
  'yarn-outward',
  'taka-inventory',
  'beam-master',
]);

const GROUP_LABELS: Record<string, string> = {
  main: 'Main Modules',
  inventory: 'Inventory',
  textile: 'Textile Modules',
};

// Challan and Bill aren't separate grantable permissions — both routes are gated by
// the same 'stock' module (App.tsx), same as the sidebar only ever shows them once
// 'stock' is granted. Clarify that here so it isn't mistaken for a missing toggle.
const MODULE_DESCRIPTION_OVERRIDES: Record<string, string> = {
  stock: 'Manage yarn stock, inventory tracking, and reports — also grants Challan and Bill access.',
};

// Simplified company create/edit form for resellers: just the essentials (company
// name, login, password) plus a single access toggle per module they themselves
// have — a deliberately separate, smaller component from the SUPERADMIN admin
// form in CompanyManagement.tsx, not a branch of it.
const CreateCompanyModal: React.FC<CreateCompanyModalProps> = ({ modules, initial, onClose, onSubmit, submitting }) => {
  const [companyName, setCompanyName] = useState(initial?.companyName || '');
  const [email, setEmail] = useState(initial?.email || '');
  const [phone, setPhone] = useState(initial?.phone || '');
  const [password, setPassword] = useState('');
  const [selectedModuleIds, setSelectedModuleIds] = useState<Set<number>>(
    new Set((initial?.permissions || []).map((p) => p.moduleId))
  );

  const toggleModule = (id: number) => {
    setSelectedModuleIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name: companyName,
      email,
      phone,
      companyName,
      password,
      permissions: Array.from(selectedModuleIds).map((moduleId) => ({
        moduleId,
        canView: true,
        canCreate: false,
        canUpdate: false,
        canDelete: false,
      })),
    });
  };

  const groups: { key: string; label: string; items: ModuleOption[] }[] = [
    { key: 'main', label: GROUP_LABELS.main, items: modules.filter((m) => m.category === 'main') },
    { key: 'inventory', label: GROUP_LABELS.inventory, items: modules.filter((m) => INVENTORY_MODULE_NAMES.has(m.name)) },
    {
      key: 'textile',
      label: GROUP_LABELS.textile,
      items: modules.filter((m) => m.category !== 'main' && !INVENTORY_MODULE_NAMES.has(m.name)),
    },
  ].filter((g) => g.items.length > 0);

  const renderModuleRow = (module: ModuleOption) => {
    const Icon = MODULE_ICONS[module.name] || Package;
    const checked = selectedModuleIds.has(module.id);
    return (
      <div
        key={module.id}
        className="flex items-center justify-between gap-3 px-3 py-3 min-h-[52px] rounded-lg hover:bg-gray-50"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
            <Icon className="w-4 h-4 text-gray-500" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900">{module.displayName}</p>
            <p className="text-xs text-gray-500">{MODULE_DESCRIPTION_OVERRIDES[module.name] || module.description}</p>
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          aria-label={`${checked ? 'Revoke' : 'Grant'} access to ${module.displayName}`}
          onClick={() => toggleModule(module.id)}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
            checked ? 'bg-blue-600' : 'bg-gray-200'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              checked ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1100] p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[calc(100vh-2rem)] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-900">{initial ? 'Edit Company' : 'Create Company'}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body + footer share one scroll region, no nested scrollbars */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Company Name</label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                autoComplete="off"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-[16px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-[16px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-[16px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={initial ? 'Leave blank to keep current password' : ''}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-[16px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required={!initial}
              />
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900">Modules</h3>
              <p className="text-xs text-gray-500 mt-0.5 mb-3">
                Choose which of your own modules this company's login can use.
              </p>
              {modules.length === 0 ? (
                <p className="text-sm text-gray-500 border border-dashed border-gray-200 rounded-lg px-3 py-4 text-center">
                  You don't have access to any modules to grant yet.
                </p>
              ) : (
                <div className="space-y-4">
                  {groups.map((group) => (
                    <div key={group.key}>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1 mb-1.5">
                        {group.label}
                      </p>
                      <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                        {group.items.map(renderModuleRow)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer stays reachable regardless of module list length */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-white">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Saving…' : initial ? 'Save Changes' : 'Create Company'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCompanyModal;
